import { Injectable } from '@angular/core';

import { SessionStorageService } from '../../shared/session/session-storage/session-storage.service';
import { SessionablePersonalLoansDataModel } from '../sessionable-personal-loans-data-model';
import { PersonalLoansBorrowerDataModel } from './personal-loans-borrower-data/personal-loans-borrower-data.model';

import {
  LoanApplicationsStates,
  PersonalLoanRegistrationRequest,
  PersonalLoanApplicationResponse,
  PersonalLoanReapplyStatusResponse,
  GetBorrowerResponse,
  PromoCodeValidationResponse
} from '../../../../models';
import PersonalLoanType = PersonalLoanApplicationResponse.LoanTypeEnum;
import TouchpointTypeEnum = PromoCodeValidationResponse.TouchpointTypeEnum;
import LoanPurpose = PersonalLoanApplicationResponse.LoanPurposeEnum;
import ReapplyStatus = PersonalLoanReapplyStatusResponse.StatusEnum;
import ProjectType = PersonalLoanRegistrationRequest.ProjectTypeEnum;
import { SessionScenario } from '../personal-loans-session/personal-loans-session.service';

@Injectable()
export class PersonalLoansSessionDataModel extends SessionablePersonalLoansDataModel {

  private _loanType: PersonalLoanType;
  private _loanAmount: number;
  private _loanPurpose: LoanPurpose;
  private _projectType: ProjectType;
  private _promoCode: string;
  private _isBetaPeriod: boolean;
  private _touchpointType: TouchpointTypeEnum;
  private _loanApplicationGuid: string;
  private _loanState: LoanApplicationsStates.PersonalStateEnum;
  private _sessionScenario: SessionScenario;
  private _leadNumber: string;
  private _loanNumber: number;
  private _borrowerCache: GetBorrowerResponse;
  private _loanSubmitted: boolean;
  private _interactionGuid: string;
  private _reapplyStatus: ReapplyStatus;
  private _oopsNotRecognized: boolean;

  constructor(protected sessionStorageService: SessionStorageService,
              private _borrowerData: PersonalLoansBorrowerDataModel) {
    super(sessionStorageService);
  }

  public populate(model?: PersonalLoansSessionDataModel): PersonalLoansSessionDataModel {
    const borrowerData = model ? model._borrowerData : null;
    this._borrowerData.populate(borrowerData);
    this._loanType = model ? model._loanType : PersonalLoanType.Personal;
    this._loanAmount = model ? model._loanAmount : 0;
    this._loanPurpose = model ? model._loanPurpose : null;
    this._promoCode = model ? model._promoCode : '';
    this._isBetaPeriod = model ? model._isBetaPeriod : false;
    this._loanApplicationGuid = model ? model._loanApplicationGuid : '';
    this._loanState = model ? model._loanState : LoanApplicationsStates.PersonalStateEnum.None;
    this._sessionScenario = model ? model._sessionScenario : SessionScenario.UserOriginated;
    this._leadNumber = model ? model._leadNumber : '';
    this._loanNumber = model ? model._loanNumber : null;
    this._borrowerCache = model ? model._borrowerCache : null;
    this._loanSubmitted = model ? model._loanSubmitted : false;
    this._touchpointType = model ? model._touchpointType : TouchpointTypeEnum.WebPII;
    this._interactionGuid = model ? model._interactionGuid : '';
    this._reapplyStatus = model ? model._reapplyStatus : ReapplyStatus.DoNotAllow;
    this._oopsNotRecognized = model ? model._oopsNotRecognized : false;

    return this;
  }

  protected map(model: PersonalLoansSessionDataModel): PersonalLoansSessionDataModel {
    return model;
  }

  protected save(setter: (model: PersonalLoansSessionDataModel) => void): void {
    super.save(setter);
  }

  public set borrowerData(value: PersonalLoansBorrowerDataModel) {
    this._borrowerData = value;
    this.save(model => model._borrowerData = value);
  }

  public get borrowerData(): PersonalLoansBorrowerDataModel {
    return this._borrowerData;
  }

  public set loanType(value: PersonalLoanType) {
    this._loanType = value;
    this.save(model => model._loanType = value);
  }

  public get loanType(): PersonalLoanType {
    return this._loanType;
  }

  public set loanAmount(value: number) {
    this._loanAmount = value;
    this.save(model => model._loanAmount = value);
  }

  public get loanAmount(): number {
    return this._loanAmount;
  }

  public set projectType(value: ProjectType) {
    this._projectType = value;
    this.save(model => model._projectType = value);
  }

  public get projectType(): ProjectType {
    return this._projectType;
  }

  public set loanPurpose(value: LoanPurpose) {
    this._loanPurpose = value;
    this.save(model => model._loanPurpose = value);
  }

  public get loanPurpose(): LoanPurpose {
    return this._loanPurpose;
  }

  public set promoCode(value: string) {
    this._promoCode = value;
    this.save(model => model._promoCode = value);
  }

  public get promoCode(): string {
    return this._promoCode;
  }

  public set isBetaPeriod(value: boolean) {
    this._isBetaPeriod = value;
    this.save(model => model._isBetaPeriod = value);
  }

  public get isBetaPeriod(): boolean {
    return this._isBetaPeriod;
  }

  public set loanApplicationGuid(value: string) {
    this._loanApplicationGuid = value;
    this.save(model => model._loanApplicationGuid = value);
  }

  public get loanApplicationGuid(): string {
    return this._loanApplicationGuid;
  }

  public set loanState(value: LoanApplicationsStates.PersonalStateEnum) {
    this._loanState = value;
    this.save(model => model._loanState = value);
  }

  public get loanState(): LoanApplicationsStates.PersonalStateEnum {
    return this._loanState;
  }

  public set sessionScenario(value: SessionScenario) {
    this._sessionScenario = value;
    this.save(model => model._sessionScenario = value);
  }

  public get sessionScenario(): SessionScenario {
    return this._sessionScenario;
  }

  public set leadNumber(value: string) {
    this._leadNumber = value;
    this.save(model => model._leadNumber = value);
}

  public get leadNumber(): string {
    return this._leadNumber;
  }

  public set loanNumber(value: number) {
    this._loanNumber = value;
    this.save(model => model._loanNumber = value);
  }

  public get loanNumber(): number {
    return this._loanNumber;
  }

  public set borrowerCache(value: GetBorrowerResponse) {
    this._borrowerCache = value;
    this.save(model => model._borrowerCache = value);
  }

  public get borrowerCache(): GetBorrowerResponse {
    return this._borrowerCache;
  }

  public set loanSubmitted(value: boolean) {
    this._loanSubmitted = value;
    this.save(model => model._loanSubmitted = value);
  }

  public get loanSubmitted(): boolean {
    return this._loanSubmitted;
  }

  public set touchpointType(value: TouchpointTypeEnum) {
    this._touchpointType = value;
    this.save(x => x._touchpointType = value);
  }

  public get touchpointType(): TouchpointTypeEnum {
    return this._touchpointType;
  }

  public set interactionGuid(value: string) {
    this._interactionGuid = value;
  }

  public get interactionGuid(): string {
    return this._interactionGuid;
  }

  public set reapplyStatus(value: ReapplyStatus) {
    this._reapplyStatus = value;
    this.save(x => x._reapplyStatus = value);
  }

  public get reapplyStatus(): ReapplyStatus {
    return this._reapplyStatus;
  }

  public set oopsNotRecognized(value: boolean) {
    this._oopsNotRecognized = value;
    this.save(model => model._oopsNotRecognized = value);
  }

  public get oopsNotRecognized(): boolean {
    return this._oopsNotRecognized;
  }
}
